/**
 * BoardTab — the "Board" tab of the Studio workspace: agendas + minutes for corporate governance
 * meetings (Board of Directors). See docs/BOARD.md.
 *
 * List → detail, same split as RoleProfilesTab. A meeting is fully editable (agenda, attendance,
 * minutes, motions) while `status === 'draft'`; "Finalize & Sign" certifies it — pulls the
 * signer's saved personal signature (the same subsystem ASIs use), renders the minutes PDF, and
 * locks the record server-side (api/board-meetings.js rejects any further edit once finalized).
 *
 * This tool captures the standard elements a board record needs (notice, quorum, motions, votes,
 * resolutions, certification) as a recordkeeping aid — it does not itself determine legal
 * compliance for Alder James; see docs/BOARD.md.
 */

import { useState } from 'react'
import { getMySignature } from '../services/userSignature.js'
import { createBoardMeeting, deleteBoardMeeting, finalizeBoardMeeting, updateBoardMeeting } from '../services/boardMeetings.js'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import BoardMeetingEditModal from './BoardMeetingEditModal.jsx'

const MEETING_TYPE_LABEL = { annual: 'Annual', special: 'Special', quarterly: 'Quarterly' }
const isEmpty = v => v == null || v === '' || (Array.isArray(v) && v.length === 0)

function fmtDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' +
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/** meetings: array | null (null = loading). admin: bool. onMeetingsChange(nextArray). */
export default function BoardTab({ meetings, admin, onMeetingsChange }) {
  const [selectedId, setSelectedId] = useState(null)
  const [creating, setCreating] = useState(false)

  const selected = meetings && selectedId ? meetings.find(m => m.id === selectedId) : null

  function patchOne(updated) {
    onMeetingsChange(meetings.map(m => m.id === updated.id ? updated : m))
  }

  async function onDelete(m) {
    if (!(await confirmDialog({ title: 'Delete meeting?', message: `Delete "${m.title}"? This cannot be undone.`, confirmLabel: 'Delete', danger: true }))) return
    try {
      await deleteBoardMeeting(m.id)
      onMeetingsChange(meetings.filter(x => x.id !== m.id))
      if (selectedId === m.id) setSelectedId(null)
    } catch (e) {
      alertDialog({ title: 'Delete failed', message: e.message })
    }
  }

  return (
    <div style={S.panel}>
      {meetings === null ? (
        <div style={S.loading}>Loading…</div>
      ) : selected ? (
        <MeetingDetail
          meeting={selected}
          admin={admin}
          onBack={() => setSelectedId(null)}
          onSaved={patchOne}
        />
      ) : (
        <MeetingList
          meetings={meetings}
          admin={admin}
          onSelect={setSelectedId}
          onNew={() => setCreating(true)}
          onDelete={onDelete}
        />
      )}

      {creating && (
        <CreateMeetingModal
          onClose={() => setCreating(false)}
          onCreated={m => { onMeetingsChange([m, ...meetings]); setCreating(false); setSelectedId(m.id) }}
        />
      )}
    </div>
  )
}

/* ── List ──────────────────────────────────────────────────────────────────── */

function MeetingList({ meetings, admin, onSelect, onNew, onDelete }) {
  return (
    <div style={{ padding: '20px 24px 60px', maxWidth: 760, margin: '0 auto' }}>
      <div style={S.listHead}>
        <div style={S.listTitle}>Board Meetings</div>
        {admin && <button style={S.primaryBtn} onClick={onNew}>+ New Meeting</button>}
      </div>

      {!meetings.length ? (
        <div style={S.empty}>No board meetings yet.</div>
      ) : (
        meetings.map(m => (
          <div key={m.id} style={S.row} onClick={() => onSelect(m.id)}>
            <div style={S.rowMain}>
              <div style={S.rowTitle}>{m.title}</div>
              <div style={S.rowSub}>{MEETING_TYPE_LABEL[m.meeting_type] || m.meeting_type} · {fmtDateTime(m.scheduled_at)}</div>
            </div>
            <div style={m.status === 'finalized' ? S.chipFinal : S.chipDraft}>{m.status === 'finalized' ? 'Finalized' : 'Draft'}</div>
            {admin && m.status !== 'finalized' && (
              <button style={S.deleteBtn} onClick={e => { e.stopPropagation(); onDelete(m) }} title="Delete">&times;</button>
            )}
          </div>
        ))
      )}
    </div>
  )
}

/* ── Create modal ──────────────────────────────────────────────────────────── */

function CreateMeetingModal({ onClose, onCreated }) {
  const [meetingType, setMeetingType] = useState('quarterly')
  const [title, setTitle] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [location, setLocation] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    if (!title.trim() || !scheduledAt) { setError('Title and date/time are required.'); return }
    setSaving(true); setError('')
    try {
      const meeting = await createBoardMeeting({
        meeting_type: meetingType, title: title.trim(),
        scheduled_at: new Date(scheduledAt).toISOString(), location: location.trim() || null,
      })
      onCreated(meeting)
    } catch (e) {
      setError(e.message || 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.card} onClick={e => e.stopPropagation()}>
        <div style={M.head}><span style={M.title}>New Board Meeting</span><button style={M.x} onClick={onClose}>&times;</button></div>
        <div style={M.body}>
          {error && <div style={M.notice}>{error}</div>}
          <Field label="Meeting type">
            <select style={M.input} value={meetingType} onChange={e => setMeetingType(e.target.value)}>
              <option value="quarterly">Quarterly</option>
              <option value="special">Special</option>
              <option value="annual">Annual</option>
            </select>
          </Field>
          <Field label="Title">
            <input style={M.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Q3 2026 Board Meeting" />
          </Field>
          <Field label="Date & time">
            <input type="datetime-local" style={M.input} value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
          </Field>
          <Field label="Location">
            <input style={M.input} value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Alder James Studio, or Virtual — Zoom" />
          </Field>
        </div>
        <div style={M.foot}>
          <button style={M.ghost} onClick={onClose}>Cancel</button>
          <button style={M.primary} onClick={save} disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return <div style={M.field}><div style={M.fieldLabel}>{label}</div>{children}</div>
}

/* ── Detail ────────────────────────────────────────────────────────────────── */

function MeetingDetail({ meeting, admin, onBack, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [finalizing, setFinalizing] = useState(false)
  const draft = meeting.status !== 'finalized'

  async function onFinalize() {
    const sig = await getMySignature().catch(() => null)
    if (!sig || !sig.image_url) {
      await alertDialog({ title: 'Signature required', message: 'Add your signature in Settings (top-right menu → Settings) before certifying minutes.' })
      return
    }
    if (!(await confirmDialog({
      title: 'Finalize & sign minutes?',
      message: 'This certifies the minutes as final and signs them with your saved signature. The meeting record can no longer be edited afterward.',
      confirmLabel: 'Finalize & Sign',
    }))) return
    setFinalizing(true)
    try {
      const { meeting: updated } = await finalizeBoardMeeting(meeting.id)
      onSaved(updated)
    } catch (e) {
      alertDialog({ title: 'Could not finalize', message: e.message })
    } finally {
      setFinalizing(false)
    }
  }

  return (
    <div style={{ padding: '20px 24px 60px', maxWidth: 760, margin: '0 auto' }}>
      <div style={S.detailNav}>
        <button style={S.backBtn} onClick={onBack}>‹ All meetings</button>
        {admin && draft && <button style={S.ghostBtn} onClick={() => setEditing(true)}>Edit</button>}
      </div>

      <div style={S.detailHead}>
        <div style={S.detailTitle}>{meeting.title}</div>
        <div style={S.detailSub}>
          {MEETING_TYPE_LABEL[meeting.meeting_type] || meeting.meeting_type} Meeting · {fmtDateTime(meeting.scheduled_at)}
          {meeting.location && <> · {meeting.location}</>}
        </div>
      </div>

      {meeting.status === 'finalized' ? (
        <div style={S.finalBanner}>
          Finalized {meeting.finalized_at ? new Date(meeting.finalized_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
          {meeting.finalized_by_name && <> by {meeting.finalized_by_name}</>}.
          {meeting.pdf_url && <> <a href={meeting.pdf_url} target="_blank" rel="noreferrer" style={S.link}>View signed PDF ↗</a></>}
        </div>
      ) : (
        <div style={S.draftBanner}>Draft — editable until finalized.</div>
      )}

      <SectionHeading>Meeting Info</SectionHeading>
      <KV label="Called by" value={meeting.called_by} />
      <KV label="Notice" value={meeting.notice_waived ? 'Waived by all directors' : meeting.notice_given ? 'Given as required' : 'Not confirmed'} />

      <SectionHeading>Attendance & Quorum</SectionHeading>
      <KV label="Presiding officer" value={meeting.presiding_officer_name} />
      <KV label="Acting secretary" value={meeting.acting_secretary_name} />
      <KV label="Quorum" value={meeting.quorum_met === true ? 'Present' : meeting.quorum_met === false ? 'Not present' : 'Not confirmed'} />
      {(meeting.attendees || []).length > 0 && (
        <ul style={S.ul}>
          {meeting.attendees.map((a, i) => (
            <li key={i} style={S.li}>
              <span style={{ color: a.present ? 'var(--aj-accent)' : 'var(--aj-text-muted)', marginRight: 6 }}>{a.present ? '✓' : '–'}</span>
              {a.name}{a.title ? ` — ${a.title}` : ''}{a.is_director ? ' (Director)' : ''}
            </li>
          ))}
        </ul>
      )}

      {(meeting.agenda || []).length > 0 && (
        <>
          <SectionHeading>Agenda</SectionHeading>
          <ol style={S.ol}>
            {meeting.agenda.map(item => (
              <li key={item.id} style={S.li}>{item.title}{item.presenter ? ` (${item.presenter})` : ''}</li>
            ))}
          </ol>
        </>
      )}

      {(meeting.agenda || []).length > 0 && (
        <>
          <SectionHeading>Minutes</SectionHeading>
          {meeting.agenda.map((item, i) => {
            const m = (meeting.minutes || []).find(x => x.agenda_item_id === item.id) || {}
            return (
              <div key={item.id} style={S.minuteBlock}>
                <div style={S.minuteTitle}>{i + 1}. {item.title}</div>
                {m.discussion && <div style={S.minuteDiscussion}>{m.discussion}</div>}
                {(m.motions || []).map((mo, j) => (
                  <div key={j} style={S.motion}>
                    <div>Motion{mo.moved_by ? ` by ${mo.moved_by}` : ''}{mo.seconded_by ? `, seconded by ${mo.seconded_by}` : ''}: {mo.text}</div>
                    <div style={S.motionResult}>Result: {mo.result || 'Not recorded'}</div>
                  </div>
                ))}
                {m.resolution_text && <div style={S.resolution}>RESOLVED: {m.resolution_text}</div>}
              </div>
            )
          })}
        </>
      )}

      {!isEmpty(meeting.other_business) && (
        <>
          <SectionHeading>Other Business</SectionHeading>
          <div style={S.plainText}>{meeting.other_business}</div>
        </>
      )}

      <SectionHeading>Adjournment</SectionHeading>
      <div style={S.plainText}>
        {meeting.adjourned_at ? `Adjourned at ${new Date(meeting.adjourned_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}.` : 'Not yet adjourned.'}
      </div>

      {admin && draft && (
        <button style={S.finalizeBtn} onClick={onFinalize} disabled={finalizing}>
          {finalizing ? 'Finalizing…' : 'Finalize & Sign'}
        </button>
      )}

      {editing && (
        <BoardMeetingEditModal
          meeting={meeting}
          onClose={() => setEditing(false)}
          onSaved={updated => { setEditing(false); onSaved(updated) }}
        />
      )}
    </div>
  )
}

function SectionHeading({ children }) { return <div style={S.sectionHeading}>{children}</div> }
function KV({ label, value }) {
  if (isEmpty(value)) return null
  return <div style={S.kv}><span style={S.kvLabel}>{label}:</span> {value}</div>
}

const S = {
  panel: { display: 'flex', flexDirection: 'column' },
  loading: { padding: 40, textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 13 },
  empty: { padding: '40px 0', textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 13 },

  listHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 },
  listTitle: { fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 22, letterSpacing: '0.02em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  primaryBtn: { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 14px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff' },

  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderRadius: 8, border: '0.5px solid var(--aj-border)', marginBottom: 8, cursor: 'pointer', background: 'var(--aj-bg-elevated)' },
  rowMain: { flex: 1 },
  rowTitle: { fontFamily: '"DM Sans",sans-serif', fontWeight: 600, fontSize: 13.5, color: 'var(--aj-text)' },
  rowSub: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 },
  chipDraft: { fontFamily: '"DM Mono",monospace', fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8a6508', background: 'rgba(184,134,11,0.12)', padding: '3px 8px', borderRadius: 10 },
  chipFinal: { fontFamily: '"DM Mono",monospace', fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--aj-accent)', background: 'rgba(29,158,117,0.12)', padding: '3px 8px', borderRadius: 10 },
  deleteBtn: { background: 'none', border: 'none', color: 'var(--aj-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 4px' },

  detailNav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  backBtn: { background: 'none', border: 'none', color: 'var(--aj-text-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, cursor: 'pointer', padding: 0 },
  ghostBtn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 12px', cursor: 'pointer' },
  detailHead: { marginBottom: 12 },
  detailTitle: { fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 22, letterSpacing: '0.02em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  detailSub: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', marginTop: 4 },

  draftBanner: { background: 'rgba(184,134,11,0.1)', border: '0.5px solid rgba(184,134,11,0.3)', borderRadius: 6, padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#8a6508', marginBottom: 16 },
  finalBanner: { background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.3)', borderRadius: 6, padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-accent)', marginBottom: 16 },
  link: { color: 'inherit', textDecoration: 'underline' },

  sectionHeading: { fontFamily: 'Montserrat,sans-serif', fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginTop: 20, marginBottom: 8, paddingBottom: 6, borderBottom: '0.5px solid var(--aj-border)' },
  kv: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', marginBottom: 4 },
  kvLabel: { color: 'var(--aj-text-muted)' },
  ul: { margin: '6px 0 0', paddingLeft: 20 },
  ol: { margin: 0, paddingLeft: 20 },
  li: { fontFamily: '"DM Sans",sans-serif', fontSize: 13.5, lineHeight: 1.5, color: 'var(--aj-text)', marginBottom: 5 },

  minuteBlock: { marginBottom: 14 },
  minuteTitle: { fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: 13.5, color: 'var(--aj-text)', marginBottom: 4 },
  minuteDiscussion: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, color: 'var(--aj-text)', marginBottom: 6 },
  motion: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5, color: 'var(--aj-text)', paddingLeft: 12, marginBottom: 4 },
  motionResult: { fontStyle: 'italic', color: 'var(--aj-text-mid)', fontSize: 12 },
  resolution: { fontFamily: '"DM Sans",sans-serif', fontWeight: 700, fontSize: 13, color: 'var(--aj-text)', paddingLeft: 12, marginTop: 4 },
  plainText: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, color: 'var(--aj-text)' },

  finalizeBtn: { marginTop: 24, background: '#07272D', border: 'none', borderRadius: 4, padding: '9px 18px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' },
}

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 700, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' },
  card: { width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border-mid)' },
  title: { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)' },
  x: { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' },
  body: { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border-mid)' },
  field: { display: 'flex', flexDirection: 'column' },
  fieldLabel: { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 4 },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)' },
  primary: { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' },
  ghost: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' },
  notice: { background: 'rgba(184,134,11,0.1)', border: '0.5px solid rgba(184,134,11,0.3)', borderRadius: 6, padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#8a6508' },
}
